#!/usr/bin/env node
import { spawnSync } from "node:child_process";
import { existsSync, readFileSync, statSync } from "node:fs";
import { basename, join, resolve } from "node:path";
import { Command, CommanderError } from "commander";
import type { CommandContext } from "./commands/Command";
import type { HermesAgentContext } from "./commands/hermes/types";
import { SOUL_TONES } from "./commands/hermes/types";
import { EnsureTemplateConfig } from "./commands/hermes/EnsureTemplateConfig";
import {
  RECIPE_REGISTRY,
  COMMAND_REGISTRY,
  getRecipeNames,
  getRecipeInfo,
  getCommandNames,
  getCommandInfo,
  getCommandsByGroup,
} from "./utils/registry";
import { cancel, multiselect, text, isCancel } from "@clack/prompts";
import { recipeRegistry, lifecycleContext, runAudit, runMigration, runMigrationForRules, formatAuditReport, formatMigrationReport, formatRulePicker, getParityRuleIds, runMomoReadinessAudit, formatMomoReadinessReport, type AuditFinding } from "./parity/index";
import {
  boardDelivery,
  doctorProjectRegistry,
  formatProjectInitPlan,
  formatProjectList,
  getProject,
  loadProjectRegistry,
  planProjectInit,
  projectRegistryPath,
  removeProjectRecord,
  type BoardDelivery,
} from "./project/index";
import { resolveBoardUrl } from "./project/boardUrl";
import { formatIdentityReport, linkProjectBoard, reconcileProjectIdentity } from "./project/identity";
import {
  BoardError,
  fetchModules,
  fetchRecentTickets,
  fetchStartedTickets,
  resolveBoardContext,
  resolveProject,
  resolveProvider,
} from "./project/boardQuery";
import { formatModuleList, formatTicketTable } from "./project/boardRender";
import { openUrl } from "./project/openUrl";
import { describeProject, formatProjectDescription } from "./describe/index";
import { computeRepoActivityBatch } from "./describe/activity";
import { runChecklist } from "./describe/checklist";
import type { ProjectRecipeInput, ProjectRecipeResult } from "./recipes/ProjectRecipe";
import { projectNotebookDryRunProjection } from "./recipes/NotebookRecipe";
import { PJANGLER_VERSION } from "./utils/version";
import { bold, cyan, dim, green, red, yellow, glyph, heading } from "./utils/style";
import type { MigrationReport } from "./parity/index";
import { isNotebookJsonInvocation, notebookParserFailureEnvelope, registerNotebookCli } from "./notebook/cli";
import { notebookEnvelopeExitCode, renderNotebookJson } from "./notebook/output";

/** Red ✖ prefix for user-facing error lines. */
const xmark = `${red(glyph.fail)}`;

type JsonObject = Record<string, unknown>;

interface ProjectInitCliOptions {
  description?: string;
  targetDir?: string;
  sourceSkill?: string;
  primaryLanguage?: string;
  provisionAgent?: boolean;
  agentRole?: string;
  apply?: boolean;
  dryRun?: boolean;
  live?: boolean;
  slug?: string;
  identifier?: string;
  ticketProvider?: string;
  boardId?: string;
  boardUrl?: string;
  skipBoard?: boolean;
  workspace?: string;
  registry?: string;
  force?: boolean;
  yes?: boolean;
  tui?: boolean;
  json?: boolean;
}

interface ProjectInitSelection {
  selectedOperations: string[];
  selectedParityRules: string[];
}

function printMigrationReport(report: MigrationReport, asJson: boolean): void {
  if (asJson) {
    console.log(JSON.stringify(report, null, 2));
  } else {
    console.log(formatMigrationReport(report));
  }
}

async function promptForRuleIds(rules: AuditFinding[]): Promise<string[]> {
  // Which rules are offered, and in what order, is unchanged — only how they
  // are rendered. formatRulePicker is pure presentation over this same list.
  const fixable = rules.filter((rule) => rule.fixable);
  const { message, options } = formatRulePicker(fixable);

  if (!options.length) {
    return [];
  }

  const initialValues = fixable
    .filter((rule) => rule.status !== "pass" && rule.status !== "skip")
    .map((rule) => rule.id);

  const selected = await multiselect<string>({
    message,
    options,
    initialValues,
  });

  if (isCancel(selected)) {
    return [];
  }

  return selected;
}

function readJson(path: string): JsonObject | undefined {
  if (!existsSync(path)) return undefined;
  try {
    const parsed = JSON.parse(readFileSync(path, "utf8")) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as JsonObject) : undefined;
  } catch {
    return undefined;
  }
}

function findGitRoot(cwd: string): string | undefined {
  const result = spawnSync("git", ["rev-parse", "--show-toplevel"], { cwd, encoding: "utf8" });
  if (result.status !== 0) return undefined;
  return resolve(result.stdout.trim());
}

function packageNameToProjectName(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const name = value.split("/").pop() ?? value;
  return name
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();
}

function deriveProjectDefaults(targetDir: string): { name: string; description: string; slug?: string; identifier?: string } {
  const manifest = readJson(join(targetDir, ".project.json"));
  const pkg = readJson(join(targetDir, "package.json"));
  const name =
    String(manifest?.project_name ?? "").trim() ||
    packageNameToProjectName(typeof pkg?.name === "string" ? pkg.name : undefined) ||
    packageNameToProjectName(basename(targetDir)) ||
    "Project";
  const ticketProvider = manifest?.ticket_provider && typeof manifest.ticket_provider === "object" ? (manifest.ticket_provider as JsonObject) : {};
  return {
    name,
    description: String(manifest?.project_description ?? pkg?.description ?? ""),
    slug: typeof manifest?.project_slug === "string" ? manifest.project_slug : undefined,
    identifier: typeof ticketProvider.identifier === "string" ? ticketProvider.identifier : undefined,
  };
}

function isInteractiveProjectInit(options: ProjectInitCliOptions): boolean {
  return !options.json && !options.yes && options.tui !== false && Boolean(process.stdin.isTTY && process.stdout.isTTY);
}

async function promptTextValue(message: string, initialValue?: string): Promise<string> {
  const value = await text({
    message,
    initialValue,
    validate: (input) => input?.trim() ? undefined : "Required",
  });
  if (isCancel(value)) {
    cancel("project init cancelled");
    process.exit(1);
  }
  return value.trim();
}

/**
 * Say — unmissably — whether this init ended with a board the provider
 * confirmed.
 *
 * The reported defect was an ingress that "seemed to work without incident"
 * while silently doing less than the operator believed: no board, `board_id`
 * left "", exit 0, and not one line of output about any of it. Eleven of
 * twenty-four registry records were built that way.
 *
 * So the rule is now: `pj init` claims success only when the board is
 * confirmed. `--skip-board` is the one path that is allowed to end unlinked,
 * because the operator asked for it — it still prints a warning block, it just
 * does not fail. Everything else is a failed ingress and exits non-zero, with
 * the two commands that actually fix it.
 *
 * Returns true when the ingress delivered what it promised.
 */
function reportBoardDelivery(board: BoardDelivery, slug: string, skipRequested: boolean): boolean {
  if (board.confirmed) {
    console.log(`  ${green(glyph.pass)} ${bold("Board linked")}  ${dim(glyph.dot)}  ${cyan(`${board.provider}/${board.workspace}/${board.identifier}`)}  ${dim(board.boardId)}`);
    return true;
  }
  const mark = skipRequested ? `${yellow(glyph.warn)}` : xmark;
  const title = skipRequested ? "No ticket board (--skip-board)" : "No ticket board";
  const log = skipRequested ? console.log : console.error;
  log("");
  log(`  ${mark} ${bold(title)}  ${dim(glyph.dot)}  ${cyan(slug)}`);
  log(`     ${dim(`the ${board.provider} board was not confirmed; the record stays state=${board.state} with board_id ""`)}`);
  // Only a SUPPRESSED action needs its plan reason repeated. When the action
  // ran and came back empty, the adapter's own log line above already said why,
  // and echoing the intent underneath it reads like a contradiction.
  if (!board.intended && board.reason) log(`     ${dim(`why: ${board.reason}`)}`);
  if (!skipRequested) {
    log(`     ${dim("fix:")} ${cyan(`pj project link ${slug} <board-id> --apply`)} ${dim("to bind an existing board")}`);
    log(`     ${dim("  or")} ${cyan(`pj init --target-dir <repo>`)} ${dim("again once the provider credential resolves")}`);
    log(`     ${dim("  or")} ${cyan("--skip-board")} ${dim("if this project is meant to have none")}`);
  }
  log("");
  return skipRequested;
}

function projectInitActionLabel(kind: string): string {
  switch (kind) {
    case "registry.upsert":
      return "Register/update project registry entry";
    case "copier.copy.commonproject":
      return "Render CommonProject scaffold";
    case "project.write-manifest":
      return "Write repo-local .project.json projection";
    case "ticket-provider.create-or-link":
      return "Create/link ticket provider project";
    case "hermes.provision-agent":
      return "Provision Hermes agent";
    default:
      return kind;
  }
}

function registryNeedsUpsert(plan: ReturnType<typeof planProjectInit>): boolean {
  const registry = loadProjectRegistry(plan.registryPath);
  const existing = registry.projects[plan.project.slug];
  if (!existing) return true;
  const { created_at: _existingCreated, updated_at: _existingUpdated, ...existingComparable } = existing;
  const { created_at: _projectCreated, updated_at: _projectUpdated, ...projectComparable } = plan.project;
  return JSON.stringify(existingComparable) !== JSON.stringify(projectComparable);
}

function actionNeedsRun(plan: ReturnType<typeof planProjectInit>, kind: string, syncMode: boolean): boolean {
  if (kind === "registry.upsert") return registryNeedsUpsert(plan);
  if (kind === "project.write-manifest") {
    const action = plan.actions.find((item) => item.kind === "project.write-manifest");
    if (!action || action.kind !== "project.write-manifest") return false;
    const next = `${JSON.stringify(action.manifest, null, 2)}\n`;
    return !existsSync(action.path) || readFileSync(action.path, "utf8") !== next;
  }
  if (kind === "copier.copy.commonproject") return true;
  if (kind === "ticket-provider.create-or-link") return plan.actions.some((action) => action.kind === kind && action.enabled);
  if (kind === "hermes.provision-agent") return plan.actions.some((action) => action.kind === kind && action.enabled);
  return true;
}

async function selectProjectInitOperations(input: {
  plan: ReturnType<typeof planProjectInit>;
  auditRules: AuditFinding[];
  syncMode: boolean;
  options: ProjectInitCliOptions;
}): Promise<ProjectInitSelection> {
  const planOperations = input.plan.actions
    .filter((action) => actionNeedsRun(input.plan, action.kind, input.syncMode))
    .map((action) => ({
      value: action.kind,
      label: projectInitActionLabel(action.kind),
      hint: action.kind === "registry.upsert" ? input.plan.registryPath : action.kind,
    }));
  const parityOperations = input.auditRules
    .filter((rule) => rule.fixable && rule.status !== "pass" && rule.status !== "skip")
    .map((rule) => ({
      value: `parity:${rule.id}`,
      label: `${rule.title}`,
      hint: `${rule.id}: ${rule.summary}`,
    }));
  const operations = [...planOperations, ...parityOperations];
  const all = operations.map((operation) => operation.value);
  if (input.options.yes || (input.options.apply && !isInteractiveProjectInit(input.options))) {
    return {
      selectedOperations: all,
      selectedParityRules: parityOperations.map((operation) => operation.value.replace(/^parity:/, "")),
    };
  }
  if (input.options.dryRun || !isInteractiveProjectInit(input.options)) {
    return { selectedOperations: [], selectedParityRules: [] };
  }
  if (!operations.length) return { selectedOperations: [], selectedParityRules: [] };
  const selected = await multiselect<string>({
    message: "Select project init operations to run:",
    options: operations,
    initialValues: all,
  });
  if (isCancel(selected)) {
    cancel("project init cancelled");
    process.exit(1);
  }
  return {
    selectedOperations: selected,
    selectedParityRules: selected.filter((value) => value.startsWith("parity:")).map((value) => value.replace(/^parity:/, "")),
  };
}

async function resolveProjectInitTarget(name: string | undefined, options: ProjectInitCliOptions): Promise<{ name: string; targetDir: string; description: string; syncMode: boolean; slug?: string; identifier?: string }> {
  const interactive = isInteractiveProjectInit(options);
  const cwd = process.cwd();
  const cwdGitRoot = findGitRoot(cwd);
  let targetDir = options.targetDir ? resolve(options.targetDir) : undefined;

  if (!targetDir && cwdGitRoot) {
    targetDir = cwdGitRoot;
  }

  if (!targetDir && interactive) {
    const defaultName = name ?? basename(cwd);
    const promptedName = name ?? await promptTextValue("Project name", packageNameToProjectName(defaultName));
    const defaultDir = join(cwd, promptedName.replace(/[^A-Za-z0-9._-]/g, "") || promptedName.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
    targetDir = await promptTextValue("Project directory", defaultDir);
    name = promptedName;
  }

  if (!targetDir) {
    if (!name) throw new Error("Project name or --target-dir is required when project init is not run inside a git repo");
    targetDir = resolve(process.cwd(), name.replace(/[^A-Za-z0-9._-]/g, "") || name.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
  }

  const targetExists = existsSync(targetDir);
  if (targetExists && !statSync(targetDir).isDirectory()) throw new Error(`Target path is not a directory: ${targetDir}`);
  const targetGitRoot = targetExists ? findGitRoot(targetDir) : undefined;
  // PJAN-84: adopt anything that is ALREADY a pjangler project, not only a git root.
  //
  // `syncMode` used to mean "this directory is a git root". A failed init over a
  // pre-existing target leaves an orphan carrying .project.json,
  // .copier-answers.yml, mise.toml, _bmad/ and the CLI roots but NO .git and no
  // registry row — so re-planning it reported `mode: create` and proposed
  // `copier.copy.commonproject` again, re-rendering the template over a
  // populated tree. Every retry produced the same orphan, which is what "fails
  // in a new way on every repo" actually was.
  //
  // `.project.json` is the canonical manifest and the thing `sot.project-json`
  // owns; `.copier-answers.yml` is proof a render already happened. Either one
  // means this is not greenfield, so finish what is here instead of starting
  // over. Copier's post-render tasks were made idempotent in PJAN-82, so an
  // adopting re-render no longer destroys .gitignore or a hand-written
  // CLAUDE.md — which is what made adoption safe to do at all.
  const alreadyScaffolded = targetExists && (
    existsSync(join(targetDir, ".project.json"))
    || existsSync(join(targetDir, ".copier-answers.yml"))
  );
  const syncMode = Boolean(
    (targetGitRoot && resolve(targetGitRoot) === resolve(targetDir))
    || alreadyScaffolded,
  );

  const defaults = targetExists ? deriveProjectDefaults(targetDir) : { name: packageNameToProjectName(basename(targetDir)) ?? "Project", description: "" };
  if (!name && interactive && !syncMode) {
    name = await promptTextValue("Project name", defaults.name);
  }

  return {
    name: name ?? defaults.name,
    targetDir,
    description: options.description ?? defaults.description,
    syncMode,
    slug: options.slug ?? defaults.slug,
    identifier: options.identifier ?? defaults.identifier,
  };
}

/** Shared body for scaffolding a single subsystem/recipe into the cwd. Used by
 * `pjangler add`, `pjangler recipe run`, and the deprecated `pjangler init <subsystem>`. */
async function runRecipeSubsystem(name: string, options: { force?: boolean; dryRun?: boolean }): Promise<void> {
  const context: CommandContext = {
    targetDir: process.cwd(),
    force: options.force || false,
    dryRun: options.dryRun || false,
  };
  try {
    if (!recipeRegistry.get(name)) {
      console.error(`${xmark} Unknown subsystem: ${bold(name)}`);
      console.error(`  ${dim("Available:")} ${getRecipeNames().map((available) => cyan(available)).join(dim(", "))}`);
      process.exit(1);
    }
    const result = await recipeRegistry.initRecipe(
      name,
      lifecycleContext(context.targetDir, Boolean(context.dryRun), false, context),
      {},
    );
    for (const line of result.logs) console.log(line.split("\n").map((part) => part ? `  ${part}` : part).join("\n"));
    for (const error of result.errors) console.error(`${xmark} ${error}`);
    if (!result.ok) process.exitCode = 1;
  } catch (error) {
    console.error(`${xmark} Error scaffolding ${bold(name)}:`, error);
    process.exit(1);
  }
}

const program = new Command();
const commandArgs = process.argv.slice(2);
program.exitOverride();
program.configureOutput({
  writeErr: (text) => { if (!isNotebookJsonInvocation(commandArgs)) process.stderr.write(text); },
});
registerNotebookCli(program);

program
  .name("pjangler")
  .description("Project subsystem bootstrapper CLI")
  .version(PJANGLER_VERSION);

// ============================================================================
// INIT COMMAND
// ============================================================================

program
  .command("init")
  .argument("[name]", "Project name to bootstrap (omit inside an existing git repo)")
  .description("Bootstrap a project: registry entry + CommonProject scaffold + .project.json")
  .option("--description <text>", "Project description")
  .option("--target-dir <path>", "Target repo path")
  .option("--source-skill <path>", "Source skill/template provenance path")
  .option("--primary-language <language>", "Primary language for CommonProject rendering", "python")
  .option("--provision-agent", "Plan local Hermes PM agent provisioning")
  .option("--agent-role <role>", "Hermes agent role to plan when --provision-agent is set", "pm")
  .option("--apply", "Write the registry and render the repo scaffold")
  .option("--dry-run", "Preview changes without writing files (default)")
  .option("--live", "Allow host-level external effects (systemd, notebook reconcile). The ticket board is created by default and does not need this.")
  .option("--slug <slug>", "Project registry slug override")
  .option("--identifier <identifier>", "Ticket identifier override")
  .option("--ticket-provider <type>", "Ticket provider: plane | trello", "plane")
  .option("--board-id <id>", "Board id (Plane project UUID or Trello board id)")
  .option("--board-url <url>", "Deprecated no-op; board URLs are derived from provider + workspace + board-id")
  .option("--skip-board", "Do not create or link a ticket board (the record stays unlinked, and init says so)")
  .option("--workspace <name>", "Ticket workspace/org (Plane workspace; blank for Trello)")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("-f, --force", "Allow replacing an existing registry entry and re-rendering files")
  .option("-y, --yes", "Apply every proposed operation without prompting")
  .option("--no-tui", "Disable interactive prompts")
  .option("--json", "Output machine-parseable JSON")
  .action(async (name: string | undefined, options: ProjectInitCliOptions) => {
    // Back-compat: `pjangler init <subsystem>` used to scaffold a component.
    // That is now `pjangler add <subsystem>`; forward with a deprecation notice.
    if (name && getRecipeNames().includes(name)) {
      if (!options.json) {
        console.error(`${yellow(glyph.warn)} ${dim(`"pjangler init ${name}" is deprecated — use "pjangler add ${name}". Forwarding…`)}`);
      }
      await runRecipeSubsystem(name, { force: options.force, dryRun: options.dryRun });
      return;
    }
    await runProjectInit(name, options);
  });

// ============================================================================
// ADD COMMAND (scaffold a subsystem/component into the current repo)
// ============================================================================

program
  .command("add")
  .argument("<subsystem>", "Subsystem to scaffold (mise, docker, node, agent-hooks, …)")
  .description("Scaffold a subsystem/component into the current repo")
  .option("--dry-run", "Preview changes without writing files")
  .option("-f, --force", "Overwrite existing files")
  .action(async (subsystem: string, options) => {
    await runRecipeSubsystem(subsystem, options);
  });

// ============================================================================
// LIST COMMAND
// ============================================================================

program
  .command("list")
  .description("List available subsystems")
  .action(() => {
    const width = Object.keys(RECIPE_REGISTRY).reduce((max, name) => Math.max(max, name.length), 0);
    console.log("");
    console.log(`  ${heading("Available subsystems")}`);
    console.log("");
    for (const [name, info] of Object.entries(RECIPE_REGISTRY)) {
      console.log(`  ${cyan(name.padEnd(width))}  ${dim(info.description)}`);
    }
    console.log("");
    console.log(`  ${dim("Examples")}`);
    for (const example of ["pj add mise", "pj add docker", "pj add node"]) {
      console.log(`     ${dim(glyph.pointer)} ${dim(example)}`);
    }
    console.log("");
  });

// ============================================================================
// BOARD (open this project's ticket board, or read from it)
//
// `board` keeps its original behaviour — an optional work-item ref, opened in
// a browser — and grows read subcommands underneath. Commander dispatches a
// matching subcommand name before it reaches the parent action handler, so
// `board provider` reads the board while `board 71` still opens PJAN-71.
//
// None of the subcommands take a project: they all walk up from the working
// directory to the nearest `.project.json`. The board you can read is the one
// you are standing in.
// ============================================================================

/** Print a board failure the way it was written, and stop. */
function boardFailure(error: unknown): never {
  const message = error instanceof BoardError || error instanceof Error ? error.message : String(error);
  console.error(`${xmark} ${message}`);
  process.exit(1);
}

/** `board recent [count]` — reject junk rather than silently defaulting. */
function parseTicketCount(raw: string | undefined, fallback: number): number {
  if (raw === undefined) return fallback;
  const value = Number(raw);
  if (!Number.isInteger(value) || value <= 0) {
    console.error(`${xmark} count must be a positive integer, got "${raw}"`);
    process.exit(1);
  }
  return value;
}

const boardCmd = program
  .command("board")
  .argument("[ref]", "Work item to open (71, PJAN-71); defaults to the current branch's ticket, then the board")
  .description("Open this project's ticket board — or one work item — in a browser")
  .option("--print", "Print the resolved URL instead of opening it")
  .action((ref: string | undefined, options: { print?: boolean }) => {
    const url = resolveBoardUrl(process.cwd(), ref);
    if (!url) {
      console.error(`${xmark} No ticket board here: need a .project.json with ticket_provider.board_id`);
      process.exit(1);
    }
    if (options.print) {
      console.log(url);
      return;
    }
    const outcome = openUrl(url);
    // The URL is always shown, opened or not. A browser that silently failed
    // to appear leaves you with nothing; a printed link leaves you with the
    // thing you actually wanted.
    console.log(`  ${cyan(glyph.pointer)} ${outcome.display}`);
    if (outcome.reason) console.log(`  ${dim(outcome.reason)}`);
  });

boardCmd
  .command("provider")
  .description("Print this project's ticket provider (plane|trello|linear)")
  .action(() => {
    try {
      console.log(resolveProvider(resolveProject(process.cwd())));
    } catch (error) {
      boardFailure(error);
    }
  });

boardCmd
  .command("slug")
  .description("Print this project's slug")
  .action(() => {
    try {
      console.log(resolveProject(process.cwd()).slug);
    } catch (error) {
      boardFailure(error);
    }
  });

boardCmd
  .command("status")
  .description("List work items in a started state, most recently added or updated first")
  .option("--json", "Emit the work items as JSON")
  .option("--limit <count>", "Show at most this many work items")
  .action(async (options: { json?: boolean; limit?: string }) => {
    try {
      const context = resolveBoardContext(process.cwd());
      const limit = options.limit === undefined ? undefined : parseTicketCount(options.limit, 0);
      const tickets = await fetchStartedTickets(context, limit);
      if (options.json) {
        console.log(JSON.stringify(tickets, null, 2));
        return;
      }
      if (!tickets.length) {
        console.log(`  ${dim(`Nothing started on ${context.slug}.`)}`);
        return;
      }
      for (const line of formatTicketTable(tickets)) console.log(line);
    } catch (error) {
      boardFailure(error);
    }
  });

boardCmd
  .command("recent")
  .argument("[count]", "How many work items to show", "5")
  .description("List the most recently added or updated work items in any state")
  .option("--json", "Emit the work items as JSON")
  .action(async (count: string | undefined, options: { json?: boolean }) => {
    try {
      const context = resolveBoardContext(process.cwd());
      const tickets = await fetchRecentTickets(context, parseTicketCount(count, 5));
      if (options.json) {
        console.log(JSON.stringify(tickets, null, 2));
        return;
      }
      if (!tickets.length) {
        console.log(`  ${dim(`No work items on ${context.slug}.`)}`);
        return;
      }
      for (const line of formatTicketTable(tickets)) console.log(line);
    } catch (error) {
      boardFailure(error);
    }
  });

boardCmd
  .command("modules")
  .description("List this project's modules")
  .option("--json", "Emit the modules as JSON")
  .action(async (options: { json?: boolean }) => {
    try {
      const context = resolveBoardContext(process.cwd());
      const modules = await fetchModules(context);
      if (options.json) {
        console.log(JSON.stringify(modules, null, 2));
        return;
      }
      if (!modules.length) {
        console.log(`  ${dim(`No modules on ${context.slug}.`)}`);
        return;
      }
      for (const line of formatModuleList(modules)) console.log(line);
    } catch (error) {
      boardFailure(error);
    }
  });

// ============================================================================
// PROJECT REGISTRY COMMANDS
// ============================================================================

const projectCmd = program
  .command("project")
  .description("Manage the pjangler project registry");

projectCmd
  .command("init")
  .argument("[name]", "Project display name")
  .description("Plan or apply a registry-backed CommonProject initialization or legacy repo sync")
  .option("--description <text>", "Project description")
  .option("--target-dir <path>", "Target repo path")
  .option("--source-skill <path>", "Source skill/template provenance path")
  .option("--primary-language <language>", "Primary language for CommonProject rendering", "python")
  .option("--provision-agent", "Plan local Hermes PM agent provisioning")
  .option("--agent-role <role>", "Hermes agent role to plan when --provision-agent is set", "pm")
  .option("--apply", "Write the registry and render the repo scaffold")
  .option("--dry-run", "Preview changes without writing files (default)")
  .option("--live", "Allow host-level external effects (systemd, notebook reconcile). The ticket board is created by default and does not need this.")
  .option("--slug <slug>", "Project registry slug override")
  .option("--identifier <identifier>", "Ticket identifier override")
  .option("--ticket-provider <type>", "Ticket provider: plane | trello", "plane")
  .option("--board-id <id>", "Board id (Plane project UUID or Trello board id)")
  .option("--board-url <url>", "Deprecated no-op; board URLs are derived from provider + workspace + board-id")
  .option("--skip-board", "Do not create or link a ticket board (the record stays unlinked, and init says so)")
  .option("--workspace <name>", "Ticket workspace/org (Plane workspace; blank for Trello)")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("-f, --force", "Allow replacing an existing registry entry and re-rendering files")
  .option("-y, --yes", "Apply every proposed operation without prompting")
  .option("--no-tui", "Disable interactive prompts")
  .option("--json", "Output machine-parseable JSON")
  .action((name: string | undefined, options: ProjectInitCliOptions) => {
    if (!options.json) console.error(`${yellow(glyph.warn)} ${dim("\"pjangler project init\" is deprecated — use \"pjangler init\".")}`);
    return runProjectInit(name, options);
  });

/** Full project bootstrap: plan + (optionally) apply the registry/scaffold/manifest. */
async function runProjectInit(name: string | undefined, options: ProjectInitCliOptions): Promise<void> {
    try {
      const target = await resolveProjectInitTarget(name, options);
      const interactive = isInteractiveProjectInit(options);
      const apply = Boolean(!options.dryRun && (options.yes || options.apply || interactive));
      const plan = planProjectInit({
        name: target.name,
        description: target.description,
        targetDir: target.targetDir,
        sourceSkill: options.sourceSkill,
        primaryLanguage: options.primaryLanguage,
        provisionAgent: options.provisionAgent ?? false,
        agentRole: options.agentRole,
        apply,
        live: options.live ?? false,
        projectSlug: target.slug,
        projectIdentifier: target.identifier,
        ticketProvider: options.ticketProvider,
        boardId: options.boardId,
        boardUrl: options.boardUrl,
        skipPlane: options.skipBoard ?? false,
        boardWorkspace: options.workspace,
        registryPath: options.registry,
        force: options.force ?? false,
        overwrite: options.force ?? false,
        cwd: process.cwd(),
        scaffold: !target.syncMode,
      });
      const preAudit = target.syncMode ? runAudit(target.targetDir) : undefined;
      const selection = await selectProjectInitOperations({
        plan,
        auditRules: preAudit?.rules ?? [],
        syncMode: target.syncMode,
        options,
      });
      const selectedPlanActionKinds = new Set(selection.selectedOperations.filter((value) => !value.startsWith("parity:")));
      const selectedPlan = {
        ...plan,
        apply,
        dryRun: !apply,
        actions: apply ? plan.actions.filter((action) => selectedPlanActionKinds.has(action.kind)) : plan.actions,
      };

      if (!apply) {
        const notebookPlan = projectNotebookDryRunProjection(plan, target.syncMode ? "sync" : "create");
        const payload = {
          ...plan,
          mode: target.syncMode ? "sync" : "create",
          audit: preAudit,
          board: boardDelivery(plan),
          notebookPlan,
          proposedOperations: [
            ...plan.actions
              .filter((action) => actionNeedsRun(plan, action.kind, target.syncMode))
              .map((action) => action.kind),
            ...(preAudit?.rules ?? [])
              .filter((rule) => rule.fixable && rule.status !== "pass" && rule.status !== "skip")
              .map((rule) => `parity:${rule.id}`),
          ],
        };
        if (options.json) console.log(JSON.stringify(payload, null, 2));
        else {
          console.log(formatProjectInitPlan(plan));
          console.log(`  ${bold("Project Notebook")} ${dim(`(${notebookPlan.phases.length} phases)`)}`);
          for (const phase of notebookPlan.phases) {
            console.log(`     ${cyan(glyph.bullet)} ${phase.id} ${dim(`[${phase.scope}/${phase.status}] ${phase.summary}`)}`);
          }
          if (payload.proposedOperations.length) {
            console.log(`  ${bold("Proposed operations")} ${dim(`(${payload.proposedOperations.length})`)}`);
            for (const operation of payload.proposedOperations) console.log(`     ${cyan(glyph.bullet)} ${operation}`);
          } else {
            console.log(`  ${green(glyph.pass)} ${dim("Project is already in parity.")}`);
          }
          console.log("");
        }
        return;
      }

      const projectInput: ProjectRecipeInput = {
        plan: selectedPlan,
        mode: target.syncMode ? "sync" : "create",
        selectedRuleIds: selection.selectedParityRules,
        selectedOperations: selection.selectedOperations,
        quiet: Boolean(options.json),
      };
      const result = await recipeRegistry.initRecipe(
        "project",
        lifecycleContext(target.targetDir, false, false, {
          force: options.force ?? false,
          live: options.live ?? false,
          quiet: Boolean(options.json),
        }),
        projectInput,
      ) as ProjectRecipeResult;
      // The board is read off the plan the transaction actually finished with
      // (ProjectRecipe refreshes it from the canonical manifest), never off the
      // plan we hoped for on the way in.
      // Actions the operator did not select are filtered out of `selectedPlan`
      // — including a suppressed board action. Read the OUTCOME off the plan
      // the transaction finished with, but the INTENT off the full plan, or a
      // `--skip-board` run loses the very reason it has no board. That
      // filtering is what made the original defect invisible.
      const board = boardDelivery({ ...result.plan, actions: plan.actions });
      const skipRequested = Boolean(options.skipBoard);
      const boardDelivered = board.confirmed || skipRequested;
      if (options.json) {
        console.log(JSON.stringify({ ...result, ok: result.ok && boardDelivered, board }, null, 2));
      } else {
        console.log(formatProjectInitPlan(selectedPlan));
        for (const line of result.logs) console.log(line);
        for (const line of result.errors) console.error(`  ${xmark} ${line}`);
        if (result.migrationReport) console.log(formatMigrationReport(result.migrationReport));
        if (result.ok && result.changedFiles.length) console.log(`  ${green(glyph.pass)} ${bold("Project synchronized")}  ${dim(glyph.dot)}  ${cyan(plan.project.slug)}`);
        if (result.ok && result.changedFiles.length === 0) console.log(`  ${green(glyph.pass)} ${dim("Already in parity")}  ${dim(glyph.dot)}  ${cyan(plan.project.slug)}`);
        reportBoardDelivery(board, plan.project.slug, skipRequested);
      }
      process.exitCode = result.ok && boardDelivered ? 0 : 1;
    } catch (err) {
      if (options.json) {
        console.log(JSON.stringify({ ok: false, error: err instanceof Error ? err.message : String(err) }, null, 2));
      } else {
        console.error(`${xmark} project init failed:`, err instanceof Error ? err.message : err);
      }
      process.exit(1);
    }
}

projectCmd
  .command("list")
  .description("List projects in the pjangler registry")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .action(async (options) => {
    try {
      const registry = loadProjectRegistry(options.registry ?? projectRegistryPath());
      if (options.json) {
        console.log(JSON.stringify(registry, null, 2));
        return;
      }
      // Scan every registered repo concurrently so ordering by real recency
      // does not cost a visible stall on a registry this size.
      const activity = await computeRepoActivityBatch(
        Object.values(registry.projects).map((project) => project.repo_path),
      );
      console.log(formatProjectList(registry, activity));
    } catch (err) {
      console.error(`${xmark} project list failed:`, err instanceof Error ? err.message : err);
      process.exit(1);
    }
  });

projectCmd
  .command("show")
  .argument("<slug>", "Project slug")
  .description("Show one project from the pjangler registry")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .action((slug: string, options) => {
    try {
      const project = getProject(loadProjectRegistry(options.registry ?? projectRegistryPath()), slug);
      if (options.json) {
        console.log(JSON.stringify(project, null, 2));
      } else {
        console.log("");
        console.log(`  ${heading(project.name)} ${dim(`(${project.slug})`)}`);
        console.log(`  ${dim(project.repo_path)}`);
        if (project.description) console.log(`  ${project.description}`);
        console.log("");
      }
    } catch (err) {
      console.error(`${xmark} project show failed:`, err instanceof Error ? err.message : err);
      process.exit(1);
    }
  });

projectCmd
  .command("remove")
  .alias("rm")
  .argument("<slug>", "Project slug to drop from the registry")
  .description("Remove a project from the pjangler registry (the repo and its board are left alone)")
  .option("--apply", "Write the removal (default is a dry run)")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .action((slug: string, options) => {
    try {
      const result = removeProjectRecord({ slug, apply: Boolean(options.apply), registryPath: options.registry });
      if (options.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }
      const provider = result.removed.ticket_provider;
      const binding = provider.board_id
        ? `${provider.type}/${provider.workspace ?? ""}/${provider.identifier ?? ""} ${dim(provider.board_id)}`
        : dim("no board");
      console.log("");
      console.log(`  ${result.apply ? green(glyph.pass) : yellow(glyph.warn)} ${bold(result.apply ? "Removed" : "Would remove")}  ${dim(glyph.dot)}  ${cyan(result.slug)} ${dim(`(${result.removed.name})`)}`);
      console.log(`  ${dim("registry".padEnd(8))} ${dim(result.registryPath)}`);
      console.log(`  ${dim("repo".padEnd(8))} ${dim(result.removed.repo_path)} ${dim("(left on disk)")}`);
      console.log(`  ${dim("board".padEnd(8))} ${binding} ${dim("(left with the provider)")}`);
      if (!result.apply) console.log(`  ${dim("dry run — re-run with --apply to write")}`);
      console.log("");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (options.json) console.log(JSON.stringify({ ok: false, error: message }, null, 2));
      else console.error(`${xmark} project remove failed: ${message}`);
      process.exit(1);
    }
  });

projectCmd
  .command("link")
  .argument("<slug>", "Project slug")
  .argument("<board-id>", "Board id the provider already owns (Plane project UUID, Trello board id)")
  .description("Bind a registry record to an existing board, reading its identity back from the provider")
  .option("--apply", "Write the binding (default is a dry run)")
  .option("--workspace <name>", "Workspace to look the board up in (default: the record's workspace)")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .action(async (slug: string, boardId: string, options) => {
    try {
      const result = await linkProjectBoard({
        slug,
        boardId,
        apply: Boolean(options.apply),
        workspace: options.workspace,
        registryPath: options.registry,
      });
      if (options.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }
      console.log("");
      console.log(`  ${result.apply ? green(glyph.pass) : yellow(glyph.warn)} ${bold(result.apply ? "Linked" : "Would link")}  ${dim(glyph.dot)}  ${cyan(slug)} ${dim(glyph.pointer)} ${cyan(result.boardId)}${result.boardName ? dim(` (${result.boardName})`) : ""}`);
      console.log(`  ${dim("provider".padEnd(18))} ${result.provider}${result.workspace ? dim(`/${result.workspace}`) : ""}`);
      console.log(`  ${dim("identifier".padEnd(18))} ${cyan(result.identifier || dim("(none)"))}${result.before.identifier && result.before.identifier !== result.identifier ? dim(`  was ${result.before.identifier}`) : ""}`);
      // The two provenance halves are separate facts and are printed as such:
      // any provider can confirm a board exists; only Plane and Linear assign
      // the key, so Trello's stays a proposal by contract, not by failure.
      console.log(`  ${dim("identifier_source".padEnd(18))} ${result.identifierSource}${result.identifierSource === "proposed" ? dim(`  (${result.provider} assigns no key)`) : ""}`);
      console.log(`  ${dim("board_confirmed_at".padEnd(18))} ${result.boardConfirmedAt}`);
      console.log(`  ${dim("state".padEnd(18))} ${result.state === "linked" ? green(result.state) : yellow(result.state)}${result.before.state !== result.state ? dim(`  was ${result.before.state}`) : ""}`);
      if (result.manifestPath) console.log(`  ${dim("manifest".padEnd(18))} ${dim(result.manifestPath)}${result.apply ? "" : dim(" (would be updated)")}`);
      if (!result.apply) console.log(`  ${dim("dry run — re-run with --apply to write")}`);
      console.log("");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (options.json) console.log(JSON.stringify({ ok: false, error: message }, null, 2));
      else console.error(`${xmark} project link failed: ${message}`);
      process.exit(1);
    }
  });

projectCmd
  .command("identity")
  .argument("[slug]", "Project slug, agent id, or repo name")
  .description("Read board identifiers back from the provider and repair the registries")
  .option("--all", "Reconcile every agent in the Hermes fleet registry")
  .option("--apply", "Write the repairs (default is a dry-run diff)")
  .option("--json", "Output machine-parseable JSON")
  .option("--hermes-registry <path>", "Hermes agents registry override (default: ~/.hermes/agents-registry.yaml)")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .action(async (slug: string | undefined, options) => {
    try {
      // No argument means "this project" — the same implicit resolution `pj board`
      // and `pj describe` use. Only when cwd is not inside a project does the
      // operator have to say which one they meant.
      let target = slug;
      if (!target && !options.all) {
        try {
          target = resolveProject(process.cwd()).slug;
        } catch {
          throw new Error(
            "not inside a pjangler project — pass a project slug, an agent id, or --all",
          );
        }
      }
      const report = await reconcileProjectIdentity({
        target,
        all: Boolean(options.all),
        apply: Boolean(options.apply),
        hermesRegistryPath: options.hermesRegistry,
        registryPath: options.registry,
      });
      console.log(options.json ? JSON.stringify(report, null, 2) : formatIdentityReport(report));
      process.exit(report.ok ? 0 : 1);
    } catch (err) {
      console.error(`${xmark} project identity failed:`, err instanceof Error ? err.message : err);
      process.exit(1);
    }
  });

projectCmd
  .command("doctor")
  .argument("[slug]", "Optional project slug")
  .description("Validate the project registry and local projections")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .action((slug: string | undefined, options) => {
    try {
      const report = doctorProjectRegistry(options.registry ?? projectRegistryPath(), slug);
      if (options.json) {
        console.log(JSON.stringify(report, null, 2));
      } else if (!report.issues.length) {
        console.log("");
        console.log(`  ${green(glyph.pass)} ${bold("Project registry OK")}  ${dim(glyph.dot)}  ${dim(report.registryPath)}`);
        console.log("");
      } else {
        console.log("");
        console.log(`  ${red(glyph.fail)} ${bold("Project registry issues")}  ${dim(glyph.dot)}  ${dim(report.registryPath)}`);
        console.log("");
        for (const issue of report.issues) {
          const mark = issue.level === "error" ? red(glyph.fail) : yellow(glyph.warn);
          console.log(`  ${mark}  ${bold(issue.slug ?? "registry")}  ${issue.message}`);
        }
        console.log("");
      }
      process.exit(report.ok ? 0 : 1);
    } catch (err) {
      console.error(`${xmark} project doctor failed:`, err instanceof Error ? err.message : err);
      process.exit(1);
    }
  });

// ============================================================================
// RECIPE COMMANDS
// ============================================================================

const recipeCmd = program
  .command("recipe")
  .description("Manage pjangler recipes");

recipeCmd
  .command("list")
  .description("List all available recipes")
  .action(() => {
    console.log("");
    console.log(`  ${heading("Recipes")}`);
    console.log("");
    for (const [name, info] of Object.entries(RECIPE_REGISTRY)) {
      console.log(`  ${cyan(bold(name))}`);
      console.log(`     ${dim(info.description)}`);
      console.log(`     ${dim("commands")}  ${info.commands.map((command) => cyan(command)).join(dim(", "))}`);
      console.log("");
    }
    console.log(`  ${dim("Usage")}`);
    console.log(`     ${dim(glyph.pointer)} ${dim("pj recipe run <name>")}`);
    console.log(`     ${dim(glyph.pointer)} ${dim("pj recipe describe <name>")}`);
    console.log("");
  });

recipeCmd
  .command("describe")
  .argument("<name>", "Recipe name")
  .description("Show detailed information about a recipe")
  .action((name: string) => {
    const info = getRecipeInfo(name);

    if (!info) {
      console.error(`${xmark} Recipe not found: ${bold(name)}`);
      console.error(`  ${dim("Available:")} ${getRecipeNames().map((available) => cyan(available)).join(dim(", "))}`);
      process.exit(1);
    }

    console.log("");
    console.log(`  ${heading(info.name)}`);
    console.log(`  ${dim(info.description)}`);
    console.log("");
    console.log(`  ${bold("Commands")}`);
    for (const command of info.commands) console.log(`     ${cyan(glyph.bullet)} ${command}`);
    console.log("");
    console.log(`  ${dim("Usage")}`);
    console.log(`     ${dim(glyph.pointer)} ${dim(`pj recipe run ${name}`)}`);
    console.log(`     ${dim(glyph.pointer)} ${dim(`pj add ${name}`)}`);
    console.log("");
  });

recipeCmd
  .command("run")
  .argument("<name>", "Recipe name")
  .description("Execute a specific recipe")
  .option("--dry-run", "Preview changes without writing files")
  .option("-f, --force", "Overwrite existing files")
  .action(async (name: string, options) => {
    await runRecipeSubsystem(name, options);
  });

// ============================================================================
// COMMAND COMMANDS
// ============================================================================

const commandCmd = program
  .command("command")
  .alias("cmd")
  .description("Manage pjangler commands");

commandCmd
  .command("list")
  .description("List all available commands")
  .option("-g, --group", "Group commands by category")
  .action((options) => {
    console.log("");
    if (options.group) {
      console.log(`  ${heading("Commands by category")}`);
      for (const [group, commands] of Object.entries(getCommandsByGroup())) {
        const width = commands.reduce((max, command) => Math.max(max, command.name.length), 0);
        console.log("");
        console.log(`  ${bold(group.toUpperCase())}`);
        for (const command of commands) {
          console.log(`     ${cyan(command.name.padEnd(width))}  ${dim(command.description)}`);
        }
      }
      console.log("");
    } else {
      const width = Object.keys(COMMAND_REGISTRY).reduce((max, name) => Math.max(max, name.length), 0);
      console.log(`  ${heading("Commands")}`);
      console.log("");
      for (const [name, info] of Object.entries(COMMAND_REGISTRY)) {
        console.log(`  ${cyan(name.padEnd(width))}  ${dim(info.description)}`);
      }
      console.log("");
    }

    console.log(`  ${dim("Usage")}`);
    console.log(`     ${dim(glyph.pointer)} ${dim("pj command list --group")}     ${dim("# group by category")}`);
    console.log(`     ${dim(glyph.pointer)} ${dim("pj command describe <name>")}  ${dim("# command details")}`);
    console.log("");
  });

commandCmd
  .command("describe")
  .argument("<name>", "Command name")
  .description("Show detailed information about a command")
  .action((name: string) => {
    const info = getCommandInfo(name);

    if (!info) {
      console.error(`${xmark} Command not found: ${bold(name)}`);
      console.error(`  ${dim("Available:")} ${getCommandNames().map((available) => cyan(available)).join(dim(", "))}`);
      process.exit(1);
    }

    const usedIn = Object.entries(RECIPE_REGISTRY)
      .filter(([, recipeInfo]) => recipeInfo.commands.includes(name))
      .map(([recipeName]) => recipeName);

    console.log("");
    console.log(`  ${heading(info.name)}`);
    console.log(`  ${dim(info.description)}`);
    console.log("");
    console.log(`  ${dim("group".padEnd(7))} ${cyan(info.group)}`);
    console.log(`  ${dim("recipes".padEnd(7))} ${usedIn.length ? usedIn.map((recipeName) => cyan(recipeName)).join(dim(", ")) : dim("(none)")}`);
    console.log("");
    console.log(`  ${dim("Part of recipe execution (not run directly).")}`);
    console.log("");
  });

commandCmd
  .command("create")
  .argument("<name>", "Command name")
  .argument("<prompt>", "Description of what the command should do")
  .description("Create a new command from template (placeholder for STORY-005)")
  .option("-t, --template <type>", "Template type (toml, json, yaml, dockerfile)")
  .option("-m, --model <model>", "LLM model to use (OpenRouter)")
  .action((name: string, prompt: string, options) => {
    console.log("");
    console.log(`  ${yellow(glyph.warn)} ${bold("Command generation coming in STORY-005")}`);
    console.log("");
    console.log(`  ${dim("Planned")}`);
    console.log(`     ${cyan(glyph.bullet)} Generate ${bold(name)} from prompt: ${dim(`"${prompt}"`)}`);
    if (options.template) console.log(`     ${cyan(glyph.bullet)} Template type: ${cyan(options.template)}`);
    if (options.model) console.log(`     ${cyan(glyph.bullet)} LLM model: ${cyan(options.model)}`);
    console.log("");
    console.log(`  ${dim("For now, manually create commands in src/commands/")}`);
    console.log("");
  });

// ============================================================================
// AUDIT COMMAND
// ============================================================================

program
  .command("audit")
  .argument("[repo]", "Path to repo to audit (default: cwd)")
  .description("Deterministic parity audit against 33god project standard")
  .option("--profile <profile>", "Audit profile, e.g. momo-lifecycle-plane (opt-in; does not affect default audit)")
  .option("--live", "Run credentialed live checks for supported profiles (only affects supported profiles such as momo-lifecycle-plane)")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .action((repo: string | undefined, options) => {
    try {
      const profile = options.profile as string | undefined;
      const live = (options.live as boolean | undefined) ?? false;
      if (profile === "momo-lifecycle-plane") {
        const report = runMomoReadinessAudit(repo, live);
        if (options.json) {
          console.log(JSON.stringify(report, null, 2));
        } else {
          console.log(formatMomoReadinessReport(report));
        }
        process.exit(report.ready ? 0 : 1);
      }
      if (profile) {
        console.error(`${xmark} Unknown audit profile: ${bold(profile)}`);
        process.exit(1);
      }
      const report = runAudit(repo, options.registry as string | undefined);
      if (options.json) {
        console.log(JSON.stringify(report, null, 2));
      } else {
        console.log(formatAuditReport(report));
      }
      process.exit(report.ok ? 0 : 1);
    } catch (err) {
      console.error(`${xmark} audit failed:`, err);
      process.exit(1);
    }
  });

// ============================================================================
// MIGRATE COMMAND
// ============================================================================

program
  .command("migrate")
  .argument("[rule-id]", "Rule ID to migrate (omit to open interactive rule selector)")
  .argument("[repo]", "Path to repo (default: cwd)")
  .description("Idempotent migration recipe for a parity rule (or open the rule selector)")
  .option("--all", "Apply every migration recipe in order")
  .option("--dry-run", "Preview changes without writing files")
  .option(
    "--accept-registry-matches",
    "Apply the proposed mapping of legacy committed .agents/skills entries into .agents/skills.json (reported only by default)"
  )
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .action(async (ruleId: string | undefined, repo: string | undefined, options) => {
    try {
      const all = options.all ?? false;
      const dryRun = options.dryRun ?? false;
      const acceptRegistryMatches = options.acceptRegistryMatches ?? false;
      const registryOverride = options.registry as string | undefined;

      if (all) {
        // When --all is used, any positional argument is the repo path, not a rule-id.
        let actualRepo = repo;
        if (ruleId && !actualRepo) {
          actualRepo = ruleId;
        }
        const report = runMigration(undefined, actualRepo, dryRun, true, acceptRegistryMatches, registryOverride);
        printMigrationReport(report, options.json);
        process.exit(report.ok ? 0 : 1);
      }

      // Explicit rule-id + repo.
      if (ruleId && repo) {
        if (!getParityRuleIds().includes(ruleId)) {
          console.error(`${xmark} Unknown parity rule: ${bold(ruleId)}`);
          process.exit(1);
        }
        const report = runMigration(ruleId, repo, dryRun, false, acceptRegistryMatches, registryOverride);
        printMigrationReport(report, options.json);
        process.exit(report.ok ? 0 : 1);
      }

      // Single valid rule-id applies to cwd.
      if (ruleId && getParityRuleIds().includes(ruleId)) {
        const report = runMigration(ruleId, undefined, dryRun, false, acceptRegistryMatches, registryOverride);
        printMigrationReport(report, options.json);
        process.exit(report.ok ? 0 : 1);
      }

      // Non-interactive JSON mode cannot show the TUI.
      if (options.json) {
        console.error(`${xmark} JSON output requires a rule-id or --all`);
        process.exit(1);
      }

      // Non-TTY environments cannot show the TUI.
      if (!process.stdin.isTTY) {
        console.error(`${xmark} Provide a rule-id, use --all, or run in an interactive terminal`);
        process.exit(1);
      }

      // No rule-id (or a lone positional that isn't a valid rule-id) opens the TUI.
      const targetRepo = ruleId ?? repo;
      const audit = runAudit(targetRepo);
      const ruleIds = await promptForRuleIds(audit.rules);
      if (!ruleIds.length) {
        console.log(`  ${cyan(glyph.info)} ${dim("No rules selected; nothing to migrate.")}`);
        process.exit(0);
      }
      const report = runMigrationForRules(ruleIds, targetRepo, dryRun, acceptRegistryMatches, registryOverride);
      printMigrationReport(report, false);
      process.exit(report.ok ? 0 : 1);
    } catch (err) {
      console.error(`${xmark} migrate failed:`, err);
      process.exit(1);
    }
  });

// ============================================================================
// HERMES-AGENT COMMAND (provision a Hermes agent role into this repo)
// ============================================================================

program
  .command("hermes-agent")
  .alias("hermes")
  .description("Render and postcondition-verify the PM agent for the current repo")
  .option("-y, --yes", "Non-interactive defaults; never overwrites an existing role without --force")
  .option("--target-repo <name>", "Target repo name (default: basename of cwd)")
  .option("--role <role>", "Agent role override (default: pm — the only role in the fleet)")
  .option("--purpose <text>", "One-line agent purpose (default: \"pm agent for <repo>\")")
  .option(`--tone <tone>`, `Personality tone (default: direct; ${SOUL_TONES.join(" | ")})`)
  .option("--model-provider <name>", 'Inference provider override ("" = inherit shared default profile)')
  .option("--model-name <name>", 'Model name override ("" = inherit shared default profile)')
  .option("--model-base-url <url>", 'Inference API base URL override ("" = inherit shared default profile)')
  .option("--model-api-mode <mode>", 'Inference API mode override ("" = inherit shared default profile)')
  .option("--model-key-env <name>", "Environment variable name holding the scoped model credential")
  .option("--skip-telegram", "Skip the Telegram wire-up (no BotFather prompt)")
  .option("--email", "Unsupported by the pinned template; rejected before any mutation")
  .option("--skip-runtime-repo", "Deprecated no-op; Hermes always uses ignored role-local runtime state")
  .option("--skip-plane", "Skip creating or linking the ticket board")
  .option("--skip-bloodbank", "Deprecated compatibility flag; Bloodbank now uses one fleet-shared Hermes gateway")
  .option("--skip-systemd", "Skip installing systemd --user units")
  .option("--local", "Local-only: defer ticket-board creation and systemd; runtime remains ignored and role-local")
  .option("--force-config", "Merge missing pinned-schema fields into the host config without replacing existing values")
  .option("--dry-run", "Preview what would run; don't execute copier")
  .option("-f, --force", "Re-render even if agents/hermes/<role>/role.yaml already exists")
  .action(async (options) => {
    const isDarwin = process.platform === "darwin";
    const local: boolean = options.local ?? false;
    const context: HermesAgentContext = {
      targetDir: process.cwd(),
      force: options.force ?? false,
      dryRun: options.dryRun ?? false,
      yes: options.yes ?? false,
      local,
      forceConfig: options.forceConfig ?? false,
      targetRepo: options.targetRepo,
      role: options.role,
      agentPurpose: options.purpose,
      soulTone: options.tone,
      modelProvider: options.modelProvider,
      modelName: options.modelName,
      modelBaseUrl: options.modelBaseUrl,
      modelApiMode: options.modelApiMode,
      modelKeyEnv: options.modelKeyEnv,
      skipTelegram: options.skipTelegram,
      // Email is opt-in only: `--email` wires it, otherwise it's never done.
      skipEmail: options.email ? false : undefined,
      // --local (and macOS, for systemd) flip external steps off by default.
      // --skip-runtime-repo is accepted above only as a deprecated no-op; the
      // canonical ignored role-local runtime is always converged.
      skipPlane: options.skipPlane ?? local,
      skipBloodbank: options.skipBloodbank ?? local,
      skipSystemd: options.skipSystemd ?? (local || isDarwin),
    };
    try {
      const lifecycle = lifecycleContext(context.targetDir, Boolean(context.dryRun), false, context);
      const result = await recipeRegistry.initRecipe("hermes-agent", lifecycle, {});
      for (const line of result.logs) console.log(line);
      for (const error of result.errors) console.error(`${xmark} ${error}`);
      if (!result.ok) process.exit(1);
    } catch (err) {
      console.error(`${xmark} hermes-agent failed:`, err);
      process.exit(1);
    }
  });

// ============================================================================
// CONFIG COMMAND (bootstrap host config for the hermes-agent template)
// ============================================================================

const configCmd = program
  .command("config")
  .description("Manage host/provisioner configuration");

configCmd
  .command("bootstrap")
  .description("Create ~/.config/hermes-agent-template/config.toml with host-correct defaults if missing")
  .option("--force", "Merge missing pinned-schema fields without replacing existing values")
  .option("--dry-run", "Show what would be written without writing")
  .action(async (options) => {
    const ctx: HermesAgentContext = {
      targetDir: process.cwd(),
      dryRun: options.dryRun ?? false,
      forceConfig: options.force ?? false,
    };
    const result = await new EnsureTemplateConfig(ctx).invoke();
    if (!result.success) {
      if (result.message) console.error(result.message);
      process.exit(1);
    }
    if (result.message) console.log(result.message);
  });

// ============================================================================
// DESCRIBE COMMAND (Project description)
// ============================================================================

program
  .command("describe")
  .argument("[repo]", "Path to the repo to describe (default: cwd)")
  .description("Describe the current project (for AI context)")
  .option("--registry <path>", `Registry path override (default: ${projectRegistryPath()})`)
  .option("--json", "Output machine-parseable JSON")
  .option("-i, --interactive", "Tick off fixable findings and apply them")
  .action(async (repo: string | undefined, options) => {
    try {
      const description = describeProject({ repoArg: repo, registryPath: options.registry });

      if (options.json) {
        if (options.interactive) {
          console.error(`${xmark} --interactive cannot be combined with --json`);
          process.exit(1);
        }
        console.log(JSON.stringify(description, null, 2));
        return;
      }

      console.log(formatProjectDescription(description));
      if (!options.interactive) return;

      // PJAN-82: the TTY refusal is a precondition of the MODE, so it is
      // checked before anything about this repo's content. It used to sit after
      // the "nothing fixable" early return, which meant `--interactive` piped
      // into anything exited 0 whenever the repo happened to be clean — the
      // guard only fired on repos that had work to do.
      // Follows `migrate`'s established precedent: refuse rather than guess.
      if (!process.stdin.isTTY) {
        console.error(`${xmark} --interactive needs a TTY; use \`pjangler migrate --all\` non-interactively`);
        process.exit(1);
      }

      // Interactive mode expands the collapsed "migrate --all" step back into
      // per-rule choices: the read-only report wants one line, but a checklist
      // is only worth showing if each finding can be ticked on its own.
      const fixable = description.subsystems
        .flatMap((subsystem) => subsystem.rules)
        .filter((rule) => (rule.status === "fail" || rule.status === "warn") && rule.fixable);

      if (!fixable.length) {
        console.log(`  ${green(glyph.pass)} ${dim("Nothing fixable to apply.")}`);
        return;
      }

      const result = await runChecklist({
        items: fixable.map((rule) => ({ id: rule.id, title: rule.id, detail: rule.summary })),
        title: "Select parity migrations to apply",
      });
      if (result.outcome === "cancel" || !result.selected.length) {
        console.log(`  ${dim("Nothing applied.")}`);
        return;
      }

      const report = runMigrationForRules(result.selected, description.repo, false, false, options.registry as string | undefined);
      printMigrationReport(report, false);
      if (!report.ok) process.exit(1);
    } catch (err) {
      console.error(`${xmark} describe failed:`, err instanceof Error ? err.message : err);
      process.exit(1);
    }
  });

try {
  await program.parseAsync();
} catch (error) {
  if (isNotebookJsonInvocation(commandArgs)) {
    const envelope = notebookParserFailureEnvelope(commandArgs);
    process.stdout.write(renderNotebookJson(envelope));
    process.exitCode = notebookEnvelopeExitCode(envelope);
  } else if (error instanceof CommanderError) {
    process.exitCode = error.exitCode;
  } else {
    throw error;
  }
}
